import React from 'react';
import { ErrorType, PaymentError } from '../../common/error';
import { useMutate } from '@lialo/common/lib/hooks/useMutate';
import { BraintreeService } from '../../components/BraintreeDropIn';
import { usePaymentServices } from '../../common/services';
import { usePaymentConfig } from '../../config/PaymentConfigProvider';
import { PaymentMethodPayload } from 'braintree-web-drop-in';
import { Price, PurchaseInfo } from './models';
import { useGetUtmParameters } from '../common';
import { FormState } from "../common/PurchaserForm";

export function useCheckout(
  braintreeService: BraintreeService | null,
  shortLink: string,
  price: Price,
  onSuccess: (purchaseInfo: PurchaseInfo) => void
) {
  const { paymentApi } = usePaymentServices();
  const { language } = usePaymentConfig();
  const utmParams = useGetUtmParameters();

  const requestPaymentMethod = async (): Promise<Partial<PaymentMethodPayload>> => {
    if (parseFloat(price.currentPrice) === 0) {
      return {};
    } else {
      if (!braintreeService) throw new PaymentError(ErrorType.BRAINTREE, 'Drop In not loaded');
      return braintreeService.requestPaymentMethod();
    }
  };

  const submit = React.useCallback(
    async (data: FormState): Promise<PurchaseInfo> => {
      const { nonce, type } = await requestPaymentMethod();
      const response = await paymentApi.checkout({
        expectedAmount: price.currentPrice,
        nonce,
        shortLink,
        language,
        couponCode: price.appliedDiscount?.code,
        utmParameters: utmParams ?? undefined,
        ...data,
      });
      await braintreeService?.teardown();
      return { paymentMethod: type ?? 'voucher', orderId: response.orderId, price };
    },
    [paymentApi, braintreeService, utmParams, language, shortLink, price]
  );

  const { loading: loadingPayment, error: paymentError, execute: onSubmit } = useMutate<
    FormState,
    PurchaseInfo,
    PaymentError
  >(submit, {
    onSuccess,
  });

  return { loadingPayment, paymentError, onSubmit } as const;
}
